<template>
  <div class="container mt-4">
    <h1>📝 TooltipDirective</h1>
    <p>
      A simple Vue 3 tooltip directive using <code>v-tooltip</code>. Supports
      text content, custom placement (<code>top</code>, <code>bottom</code>, <code>left</code>, <code>right</code>),
      custom theme via CSS variables or JS object, and optional arrow display.
    </p>

    <h2>Basic Usage</h2>
    <div class="d-flex gap-3 flex-wrap mb-3">
      <button v-tooltip="'Simple tooltip'">Hover me (default)</button>
      <button v-tooltip="{ text: 'Tooltip on bottom', theme: { placement: 'bottom' } }">
        Hover me (bottom)
      </button>
      <button
          v-tooltip="{
          text: 'Tooltip on left',
          theme: { placement: 'left', backgroundColor: '#27ae60', color: '#fff' }
        }"
      >
        Hover me (left)
      </button>
      <button
          v-tooltip="{
          text: 'Tooltip on right',
          theme: { placement: 'right', backgroundColor: '#e67e22', color: '#fff' }
        }"
      >
        Hover me (right)
      </button>
    </div>

    <h2>Custom Theme via JS Object</h2>
    <p>
      You can pass a <code>theme</code> object to style the tooltip dynamically:
    </p>
    <pre class="p-3 mb-2 bg-white text-dark">
      v-tooltip="{
        text: 'Custom styled tooltip',
        theme: {
          backgroundColor: '#3498db',
          color: '#fff',
          padding: '1em',
          fontSize: '1rem',
          borderRadius: '0.5rem',
          placement: 'top'
        }
      }"
    </pre>

    <h2>Global Styling via CSS Variables</h2>
    <p>
      Override the default tooltip appearance globally using CSS variables:
    </p>
    <pre class="p-3 mb-2 bg-white text-dark">
      :root {
        --tooltip-background-color: #222;
        --tooltip-color: #fff;
        --tooltip-padding: 0.6em;
        --tooltip-font-size: 0.85rem;
        --tooltip-border-radius: 0.4rem;
        --tooltip-arrow-color: #222;
      }
    </pre>

    <h2>Tooltip with Arrow</h2>
    <p>
      To display an arrow, ensure the JS directive sets the appropriate class based on placement:
    </p>
    <pre class="p-3 mb-2 bg-white text-dark">
      tooltipEl.className = `v-tooltip arrow-${placement} show`;
    </pre>
    <p>
      CSS already provides styles for <code>arrow-top</code>, <code>arrow-bottom</code>, <code>arrow-left</code>, <code>arrow-right</code>.
    </p>

    <h2>Dynamic Tooltip Example</h2>
    <p>Tooltip content can change dynamically with Vue reactive state:</p>
    <button
        v-tooltip="{
          text: dynamicText,
          theme: { placement: dynamicPlacement, backgroundColor: buttonColor }
        }"
        @click="changeTooltip"
    >
      Click me to change tooltip
    </button>

    <h2>Available Parameters</h2>
    <ul>
      <li><strong>text</strong> — Tooltip content (string)</li>
      <li><strong>theme</strong> — JS object with styles and optional <code>placement</code></li>
      <li><strong>placement</strong> — <code>top</code> | <code>bottom</code> | <code>left</code> | <code>right</code></li>
    </ul>

    <h2>Examples Summary</h2>
    <ul>
      <li>Simple string tooltip</li>
      <li>Tooltip with custom placement</li>
      <li>Tooltip with custom colors, padding, font size, border radius</li>
      <li>Tooltip dynamically updated via Vue state</li>
      <li>Tooltip with arrow using <code>arrow-*</code> class</li>
    </ul>
  </div>
</template>

<script>
import { ref } from 'vue';

export default {
  setup() {
    const dynamicText = ref('Initial tooltip');
    const dynamicPlacement = ref('top');
    const buttonColor = ref('#8e44ad');

    function changeTooltip() {
      dynamicText.value = 'Updated tooltip!';
      dynamicPlacement.value =
          dynamicPlacement.value === 'top' ? 'bottom' : 'top';
      buttonColor.value =
          buttonColor.value === '#8e44ad' ? '#16a085' : '#8e44ad';
    }

    return { dynamicText, dynamicPlacement, buttonColor, changeTooltip };
  }
}
</script>

<style scoped>
  .container button {
    padding: 0.5em 1em;
    border: 1px solid #ccc;
    border-radius: 0.4rem;
    cursor: pointer;
  }

  .container h1,
  .container h2 {
    margin-top: 1em;
  }

  .container pre {
    background: #f5f5f5;
    padding: 1em;
    border-radius: 0.4rem;
    overflow-x: auto;
  }

  .d-flex {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
  }
</style>